/**
 * Extender for Collections to add common data manipulation methods
 */
import { _ } from "extensions/underscore";
import { Backbone } from "extensions/backbone";
import moment from "moment";

import { store } from "common/store";

import {
    getWalkableModelValue,
    intervalToMomentDurationAliases,
    intervalToIsoFormat
} from "common/utils";

/**
 * Reduce collection by passed object
 * @example
 * this.totals({
 *    approvals_count: 0,
 *    declines_count: 0
 * });
 * @param {Object} obj schema of object to reduce to
 * @returns {*}
 */
export const totals = function(obj) {
    const makeHash = () => store.getState().common.merchant_ref + this.generateURLQueryParams() + JSON.stringify(Object.keys(obj));
    
    // Check if collection has method generateURLQueryParams to memoize values based on request params and
    // asked total map
    if (this.length && this.generateURLQueryParams) {
        this.totalsMemo = this.totalsMemo || {};
        const hash = makeHash();
        // eslint-disable-next-line eqeqeq
        if (this.totalsMemo[hash] != null) {
            return this.totalsMemo[hash];
        }
    }
    
    const result = this.reduce((memo, model) => {
        Object.entries(obj).forEach(([key, value]) => {
            const wlk = getWalkableModelValue(model, value);
            memo[key] = (memo[key] || 0) + (wlk || 0);
        });
        return memo;
    }, Object.keys(obj).reduce((memo, k) => {
        memo[k] = void 0;
        return memo;
    }, {}));
    
    if (this.length && this.generateURLQueryParams) {
        // Store calculated value in memo to reuse later
        this.totalsMemo[makeHash()] = { ...result };
    }
    
    return result;
};

/**
 * Create proxy model/collection to react when the original collection changes
 * TODO: Check for memory leaks here, and write UNIT test
 * @param {Object} options
 * @param {Array} options.events Array of events to listen for
 * @param {Array} options.proxyEvents Array of events to proxy from the original collection
 * @param {Function} options.processData Function to run over the original collection data
 *
 */
export const proxy = function(options) {
    const opts = {
        proxyStructure: this.constructor,
        events: ["reset"],
        proxyEvents: ["sync", "filtered", "fetched", "before:fetched", "fetchError"],
        processData(collection) { return collection.toJSON(); },
        ...options
    };
    
    // Use parent collection as a constructor for proxy
    let _proxy;
    if (opts.proxyStructure === this.constructor) {
        _proxy = this.clone();
        
        // Check if parent has models to parse after creating proxied version
        if (_proxy.length) {
            _proxy.reset(opts.processData(this));
        }
    } else {
        const Structure = opts.proxyStructure;
        _proxy = new Structure();
        if (this.length) {
            _proxy.set(opts.processData(this));
        }
    }
    
    opts.events.forEach((event) => {
        this.on(event, function() {
            delete _proxy._cache;
            if (_proxy instanceof Backbone.Collection) {
                _proxy.reset(opts.processData(this));
            } else if (_proxy instanceof Backbone.Model) {
                _proxy.set(opts.processData(this));
            }
        });
    });
    
    opts.proxyEvents.forEach((event) => {
        this.on(event, function(...args) {
            args.unshift(event);
            
            // Also maintain `fetched` flag that set by `fetch` method in the original data structure
            _proxy.fetched = this.fetched;
            
            // Trigger event on the proxy
            _proxy.trigger(...args);
        });
    });
    
    _proxy.fetched = this.fetched;
    _proxy.isProxy = true;
    _proxy.parent = this;
    
    return _proxy;
};

/**
 * Iterate over Backbone Collection to group models by given property, and also reduce by others
 * TODO: Write example, add UNIT TEST, and refactor to smaller targeted methods
 * @param {Object} options
 * @param {String} options.groupBy Property to group by the collection
 * @param {Boolean} [options.fillGaps = false] Iterate over groupReduced array to add gaps between elements.
 *                                             `mapToArray` must be set to true. Fix later if ever needed.
 * @param {Boolean} [options.mapToArray = true] Return an array of results. If false then return an aggregation.
 * @param {Number} [options.gapSize = 1] Defines default gap between elements
 * @param {Number} [options.gapInterval = "day"] Defines default interval period between elements
 * @param {Number} [options.initReduce = 0] Initial value for reduced components
 * @param {Function} [options.reduce = function(all, current) { return all + current; }] Reduce function
 * @returns {*}
 */
export const groupReduce = function(options) {
    const mmnt = moment.utc;
    const memoTo = "groupReduceMemo";
    
    const makeHash = () => store.getState().common.merchant_ref + this.generateURLQueryParams() + JSON.stringify(options) + JSON.stringify(Object.keys(options.reduceBy));
    
    const getInitReduce = function() {
        return _.isArray(options.initReduce) || _.isObject(options.initReduce) ? _.clone(options.initReduce) : options.initReduce;
    };
    
    const memoize = (value) => {
        this[memoTo][makeHash()] = _.clone(value);
    };
    
    options = {
        fillGaps: false,
        gapSize: 1,
        initReduce: 0,
        groupBy: null,
        mapToArray: true,
        reduceBy: [],
        memoize: true,
        gapInterval: "day",
        undefinedValue: Number.NaN,
        reduce(all, current) { return all + current; },
        ...options
    };
    
    // Check if all conditions apply for memoization: options, presense of data, and if a collection can be serialized
    options.memoize = options.memoize && this.length && this.generateURLQueryParams;
    
    if (options.memoize) {
        this[memoTo] = this[memoTo] || {};
        const hash = makeHash();
        // eslint-disable-next-line eqeqeq
        if (this[memoTo][hash] != null) {
            return this[memoTo][hash];
        }
    }
    
    const grouppedAndReduced = this.reduce((memo, model) => {
        const groupedByModelValue = model.get(options.groupBy);
        const allGroupedByValues = memo[groupedByModelValue] || {};
        
        Object.entries(options.reduceBy).forEach(([attribute, modelProperty]) => {
            let value = getWalkableModelValue(model, modelProperty);
            
            if (value === void 0 || Number.isNaN(value)) {
                value = options.undefinedValue;
            }
            
            let all;
            let initReduce = false;
            if (Number.isNaN(allGroupedByValues[attribute])) {
                all = Number.NaN;
            } else if (allGroupedByValues[attribute]) {
                all = allGroupedByValues[attribute];
            } else {
                all = getInitReduce();
                initReduce = true;
            }
            
            allGroupedByValues[attribute] = options.reduce(all, value, initReduce);
        });
        
        // Set values back to memo
        memo[groupedByModelValue] = allGroupedByValues;
        
        return memo;
    }, {});
    
    // If the caller want an object, then return result at this point
    if (!options.mapToArray) {
        memoize(grouppedAndReduced);
        return grouppedAndReduced;
    }
    /**
     * Convert object-like structure to an array
     * From:
     * {
     *    "foo1": {
     *      "bar": 1,
     *      "baz": 2
     *    },
     *    "foo2": {
     *      "bar": 3,
     *      "baz": 4
     *    }
     * }
     * To: [{"date_time": "foo1", "bar": 1, "baz": 2}, {"date_time": "foo2", "bar": 3, "baz": 4}]
     */
    let mapped = Object.entries(grouppedAndReduced).map(([key, value]) => {
        value[options.groupBy] = key;
        return value;
    });
    
    // Add missing dates to the response
    if (mapped.length && options.fillGaps && options.groupBy === "date_time") {
        // First need to sort the array as API doesn't return it sorted
        mapped = mapped.sort((a, b) => {
            const dateA = mmnt(a[options.groupBy]);
            const dateB = mmnt(b[options.groupBy]);
            
            if (dateA.isAfter(dateB)) {
                return 1;
            }
            
            if (dateA.isBefore(dateB)) {
                return -1;
            }
            
            return 0;
        });
        
        // Now convert dates to UNIX time and add missing dates considering with expected delta of one gapPeriod.
        const newGroupedArray = [];
        mapped.forEach((elt) => {
            const prevElt = _.last(newGroupedArray);
            
            if (prevElt) {
                let actualgapSize = mmnt(elt[options.groupBy]).diff(mmnt(prevElt[options.groupBy]), intervalToMomentDurationAliases[options.gapInterval]);
                
                // Temporary structure to hold empty element
                let obj = {};
                const iterator = (k) => { obj[k] = options.initReduce; };
                
                // First add any missing elements
                while (actualgapSize > options.gapSize) {
                    // Nullify temp structure because obj in not scoped to this block
                    // Get last element again as it can be automatically inserted element to cover a gap.
                    // TODO: use better functional approach to this lastElement call
                    obj = {
                        [options.groupBy]: mmnt(_.last(newGroupedArray)[options.groupBy]).add(1, options.gapInterval).utc().format(intervalToIsoFormat[options.gapInterval])
                    };
                    
                    // Go over all reducible properties and add their initial value
                    Object.keys(options.reduceBy).forEach(iterator);
                    
                    // Add to result
                    newGroupedArray.push(obj);
                    
                    // Decrease actualgapSize to go or not over the while loop again
                    actualgapSize--;
                }
            }
            
            // Add current elt to the array
            newGroupedArray.push(elt);
        });
        
        // Assign new value to mapped array to return in the next step.
        // use clone to remove ANY chance of element being referenced somewhere
        mapped = newGroupedArray.map((val) => ({ ...val }));
    }
    
    if (options.memoize) {
        memoize(mapped);
    }
    
    return mapped;
};
